import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useContext,
  useCallback,
} from 'react';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill';
import {
  EditReservation,
  FloorPlan as FloorPlanType,
  Reservation,
  Space,
  Table,
  Warning,
} from 'types/reservations';
import TableWrapper, {
  TablePointerStart,
  computeTablePosition,
} from '../TableWrapper';
import { findOverlaps, timeToBlock } from 'utils/helper';
import { CircularProgress } from '@material-ui/core';
import Typography from 'Components/Atoms/Typography';
import SpaceSwitch from './Components/SpaceSwitch';
import ShiftMessage from '../ShiftMessage';
import { useSwipeable } from 'react-swipeable';
import { useDragLayer as useReactDragLayer } from 'react-dnd';
import useBackground from 'CustomHooks/useBackground';
import { RestaurantContext } from 'Contexts/RestaurantContext';
import { ReservationContext } from 'Contexts/ReservationContext';
import useRestaurant from 'CustomHooks/useRestaurant';
import Button from 'Components/Atoms/Button';
import {
  getSharedTableCapacityInfo,
  SharedTablePool,
} from 'utils/sharedTableCapacity';
import useSharedTablePool from 'CustomHooks/useSharedTablePool';
import { isReservationVisibleInShift } from 'utils/reservationShift';

function overlaps(
  rect1: { x: number; y: number; size: number },
  rect2: { x: number; y: number; size: number },
  axis?: 'all' | 'x' | 'y',
) {
  if (axis === 'x') {
    return (
      rect1.x < rect2.x + rect2.size &&
      rect1.x + rect1.size > rect2.x &&
      Math.abs(rect1.y - rect2.y) <= Math.max(rect1.size, rect2.size) / 2
    );
  } else if (axis === 'y') {
    return (
      rect1.y < rect2.y + rect2.size &&
      rect1.size + rect1.y > rect2.y &&
      Math.abs(rect1.x - rect2.x) <= Math.max(rect1.size, rect2.size) / 2
    );
  }

  return (
    rect1.x < rect2.x + rect2.size &&
    rect1.x + rect1.size > rect2.x &&
    rect1.y < rect2.y + rect2.size &&
    rect1.size + rect1.y > rect2.y
  );
}

type EditMode = {
  start: number | null;
  end: number | null;
  guests: number | undefined;
  tables: string[];
  new: boolean;
  id: string;
} | null;

export type FloorPlanProps = {
  floorPlan: FloorPlanType | null;
  editReservation: EditReservation | null;
  onTableClick: (
    id: string,
    name: string,
    space: string,
    available?: boolean,
    switchResa?: string | 'blocked',
  ) => void;
  reservations: Reservation[];
  space: string | null;
  currentTime: string | null;
  onSpaceChange: (id: string) => void;
  currentTable: string | null;
  currentReservationTables: null | string[];
  updateSize?: string;
  loading: boolean;
  closed:
    | false
    | {
        message: string | null;
        error: boolean;
      };
  onCloseSideBar?: () => void;
  warnings: Warning[];
};

const switchReservationHelper = (
  table: Table,
  editMode: EditMode,
  reservations: Reservation[],
  countTouchAsOverlap?: boolean,
  pool?: SharedTablePool | null,
) => {
  if (!editMode?.tables?.length) return;

  if (table.shared) {
    if (editMode.start === null || editMode.end === null) return;

    const capacityInfo = getSharedTableCapacityInfo(reservations, {
      start: editMode.start,
      end: editMode.end,
      requestedGuests: editMode.guests,
      tableMax: table.max,
      tableName: table.name,
      id: editMode.id,
      pool,
    });

    return capacityInfo.remaining > 0 ? undefined : 'blocked';
  }

  const overlappingReservations = reservations.filter((r) => {
    let overlaps = findOverlaps(
      r,
      editMode.start || 0,
      editMode.end || 0,
      countTouchAsOverlap,
    );
    let sameReservation = r.id === editMode.id;
    return !!overlaps && !sameReservation;
  });

  if (!overlappingReservations.length) {
    return;
  } else if (
    overlappingReservations.length === 1 &&
    overlappingReservations[0].guests === editMode.guests
  ) {
    return editMode.new ? 'blocked' : overlappingReservations[0].id;
  } else {
    return 'blocked';
  }
};

const DRAG_SELECT_START_THRESHOLD_PX = 8;
const DRAG_SELECT_TOUCH_START_THRESHOLD_PX = 16;

// F03 — colors for multi-table reservation groups. Chosen to be reasonably
// distinct from each other in RGB space so neighboring groups contrast well.
const GROUP_COLOR_PALETTE = [
  '#10CCB9',
  '#6369D1',
  '#CCC910',
  '#49B800',
  '#EE4266',
  '#1E90FF',
  '#F59E0B',
  '#A855F7',
];

const stableHash = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

/**
 * Assigns a palette color to every table that belongs to a multi-table
 * reservation group. Groups whose tables sit close together on the floor plan
 * are given colors that are as different as possible (greedy graph coloring by
 * RGB distance), so adjacent groups never look alike. Groups that are far apart
 * may reuse the same color. Deterministic: the same reservations always yield
 * the same colors.
 */
const computeGroupColorByTable = (
  reservations: Reservation[],
  tables: Table[],
): Record<string, string> => {
  const map: Record<string, string> = {};

  // Table center + space, keyed by table name.
  const tableCoords = new Map<
    string,
    { x: number; y: number; space: string; size: number }
  >();
  tables.forEach((t) => {
    tableCoords.set(t.name, {
      x: t.coords.x,
      y: t.coords.y,
      space: t.space,
      size: t.coords.size,
    });
  });

  const groups = reservations
    .filter(
      (r) =>
        !r.done && r.status !== 'failed' && (r.tables?.length ?? 0) > 1,
    )
    .map((r) => ({
      key: r.id || (r.tables ?? []).slice().sort().join('-'),
      tables: r.tables ?? [],
    }));

  if (!groups.length) return map;

  // Stable, input-order-independent processing order.
  groups.sort((a, b) => stableHash(a.key) - stableHash(b.key));

  // Proximity radius scaled to the typical table size on this floor plan.
  const sizes = Array.from(tableCoords.values())
    .map((c) => c.size)
    .filter((s) => s > 0)
    .sort((a, b) => a - b);
  const medianSize = sizes.length ? sizes[Math.floor(sizes.length / 2)] : 40;
  const nearRadiusSq = (medianSize * 4) ** 2;

  const groupsAreNear = (
    a: { tables: string[] },
    b: { tables: string[] },
  ) => {
    for (const n1 of a.tables) {
      const c1 = tableCoords.get(n1);
      if (!c1) continue;
      for (const n2 of b.tables) {
        const c2 = tableCoords.get(n2);
        if (!c2 || c2.space !== c1.space) continue;
        const dSq = (c1.x - c2.x) ** 2 + (c1.y - c2.y) ** 2;
        if (dSq <= nearRadiusSq) return true;
      }
    }
    return false;
  };

  // Adjacency between groups that are geographically close.
  const adjacency: number[][] = groups.map(() => []);
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      if (groupsAreNear(groups[i], groups[j])) {
        adjacency[i].push(j);
        adjacency[j].push(i);
      }
    }
  }

  const paletteRgb = GROUP_COLOR_PALETTE.map(hexToRgb);
  const colorDistSq = (i: number, j: number) => {
    const a = paletteRgb[i];
    const b = paletteRgb[j];
    return (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;
  };

  // Greedy: give each group the palette color whose smallest RGB distance to
  // its already-colored near neighbors is largest (max-min contrast).
  const colorIndex: number[] = new Array(groups.length).fill(-1);
  groups.forEach((g, gi) => {
    const neighborColors = adjacency[gi]
      .map((ni) => colorIndex[ni])
      .filter((ci) => ci >= 0);

    let best = 0;
    let bestScore = -Infinity;
    for (let i = 0; i < GROUP_COLOR_PALETTE.length; i++) {
      let minDist = Infinity;
      neighborColors.forEach((ci) => {
        minDist = Math.min(minDist, colorDistSq(i, ci));
      });
      // Stable tie-break so unconstrained groups still spread across the
      // palette instead of all picking index 0.
      const tie = (stableHash(`${g.key}:${i}`) % 1000) / 1e6;
      const score = (minDist === Infinity ? 1e12 : minDist) + tie;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    colorIndex[gi] = best;
  });

  groups.forEach((g, gi) => {
    const color = GROUP_COLOR_PALETTE[colorIndex[gi]];
    g.tables.forEach((t) => {
      map[t] = color;
    });
  });

  return map;
};

const FloorPlan: React.FC<FloorPlanProps> = ({
  floorPlan,
  editReservation,
  onTableClick,
  reservations,
  space,
  currentTime,
  onSpaceChange,
  currentTable,
  updateSize = '',
  loading,
  closed = false,
  warnings,
  currentReservationTables,
  onCloseSideBar,
}) => {
  const dimensions = useResizeDetector();

  const {
    Shuffle,
    reservationSettings,
    TablePlanAssistant,
    shiftWindow,
    hideLiveResas,
  } = useContext(ReservationContext);
  const assistantActive = TablePlanAssistant.active;

  const { experimentalFlags } = useRestaurant();

  const sharedTablePool = useSharedTablePool();

  const [update, setupdate] = useState('');

  useEffect(() => {
    setTimeout(() => {
      setupdate(updateSize);
    }, 10);
  }, [updateSize]);

  const size = useMemo(() => {
    let currentSpace = floorPlan?.spaces.find((s) => s.id === space) || null;

    if (!currentSpace) {
      return null;
    } else {
      let { height = 1, width = 1 } = dimensions;

      height -= 60;

      let h = currentSpace.size.height;
      let w = currentSpace.size.width;

      return Math.min(height / h, width / w);
    }
  }, [dimensions, floorPlan, space, update]);

  const editMode = useMemo(() => {
    if (!editReservation) return null;

    let id = editReservation?.reservationId || '';

    let start = editReservation?.timeSlot?.block || null;

    let guests = editReservation?.guests;

    let reservationLength =
      editReservation.reservationLength ??
      editReservation?.timeSlot?.reservationLength ??
      null;

    let end =
      start === null || reservationLength === null
        ? null
        : start + reservationLength;

    let tables = editReservation?.tables || [];

    return {
      start,
      end,
      guests,
      tables,
      occassion: editReservation.occassion || '',
      new: !!editReservation.new,
      id,
    };
  }, [editReservation]);

  const reservationsByTable = useMemo(() => {
    const tableMap = new Map<string, Reservation[]>();

    reservations.forEach((reservation) => {
      if (
        reservation.done ||
        reservation.status === 'failed' ||
        !reservation.tables?.length
      ) {
        return;
      }

      reservation.tables.forEach((tableName) => {
        const tableReservations = tableMap.get(tableName) ?? [];
        tableReservations.push(reservation);
        tableMap.set(tableName, tableReservations);
      });
    });

    tableMap.forEach((tableReservations) => {
      tableReservations.sort(
        (a, b) => (a.startTimeInBlocks || 0) - (b.startTimeInBlocks || 0),
      );
    });

    return tableMap;
  }, [reservations]);

  // True while a reservation (not a table) is being dragged across the floor
  // plan. Drives both keeping every space mounted during the drag (so the
  // TouchBackend drag-source node survives a mid-drag space switch) and the
  // full-width SpaceSwitch button row.
  const isDraggingReservation = useReactDragLayer((monitor) => ({
    dragging: monitor.isDragging() && monitor.getItemType() === 'reservation',
  })).dragging;

  const tables = useMemo(() => {
    return (
      floorPlan?.tables
        // While a reservation is being dragged we keep EVERY space's tables
        // mounted (not just the active one). On touch devices react-dnd's
        // TouchBackend ties the running drag to the drag-source DOM node; if
        // the source space unmounts on a mid-drag space switch, that node loses
        // its parent and the drag detaches. Tables of inactive spaces are only
        // rendered hidden (see `hidden` below), so nothing changes visually.
        .filter((t) => isDraggingReservation || t.space === space)
        .map((t) => {
          const filteredReservations = reservationsByTable.get(t.name) ?? [];

          const switchReservation = switchReservationHelper(
            t,
            editMode,
            filteredReservations,
            !reservationSettings?.blocksBetweenReservations,
            sharedTablePool,
          ) as string | 'blocked' | undefined;

          return {
            ...t,
            reservations: filteredReservations,
            switchReservation,
            hidden: isDraggingReservation && t.space !== space,
          };
        }) ?? []
    );
  }, [
    space,
    reservationsByTable,
    floorPlan,
    editMode,
    isDraggingReservation,
    sharedTablePool,
  ]);

  // F03: persistent color-coding of multi-table reservation groups so staff
  // can see grouped tables at a glance without clicking a reservation.
  // Experimental — only active with the multiTableGroupColors flag (Ctrl+E).
  const groupColorsEnabled = !!experimentalFlags?.data?.multiTableGroupColors;
  const strictShiftStartFilter =
    !!experimentalFlags?.data?.strictShiftStartFilter;

  const groupColorByTable = useMemo(() => {
    if (!groupColorsEnabled) return {};
    const visibleReservations = reservations.filter((reservation) =>
      isReservationVisibleInShift(
        reservation,
        shiftWindow,
        hideLiveResas,
        currentTime,
        strictShiftStartFilter,
      ),
    );
    return computeGroupColorByTable(
      visibleReservations,
      floorPlan?.tables ?? [],
    );
  }, [
    reservations,
    floorPlan,
    groupColorsEnabled,
    strictShiftStartFilter,
    shiftWindow,
    hideLiveResas,
    currentTime,
  ]);

  // Wash rects per group color, rendered in ONE layer per color that carries
  // the 25% alpha via `opacity` while the rects themselves stay solid. The
  // alpha is applied once after the rects are composited, so washes of
  // adjacent tables in the same group merge without darker overlap seams
  // (which `${color}40` per table produced).
  const groupWashesByColor = useMemo(() => {
    const byColor: Record<
      string,
      { key: string; style: React.CSSProperties }[]
    > = {};
    if (!size) return byColor;

    tables.forEach((t) => {
      if (t.hidden) return;
      const color = groupColorByTable[t.name];
      if (!color) return;

      // Same rect as the table's wrapper, enlarged by the wash's former
      // `inset: -10%` on each side.
      const { top, left, width, height } = computeTablePosition(t, size);
      if (!byColor[color]) byColor[color] = [];
      byColor[color].push({
        key: `${t.space}-${t.id}`,
        style: {
          position: 'absolute',
          top: top - height * 0.1,
          left: left - width * 0.1,
          width: width * 1.2,
          height: height * 1.2,
          borderRadius: '22%',
          backgroundColor: color,
        },
      });
    });

    return byColor;
  }, [tables, groupColorByTable, size]);

  const tablesTouchingX = useMemo(() => {
    const tables =
      floorPlan?.tables.map((x) => ({
        name: x.name,
        coords: x.coords,
        space: x.space,
      })) ?? [];

    return tables.reduce((acc, cV, i, arr) => {
      const touching = tables
        .filter(
          (t) =>
            t.name !== cV.name &&
            t.space === cV.space &&
            overlaps(t.coords, cV.coords, 'x'),
        )
        .map((t) => t.name);

      if (touching.length) {
        return {
          ...acc,
          [cV.name]: touching,
        };
      }

      return acc;
    }, {} as Record<string, string[]>);
  }, [floorPlan]);

  const tablesTouchingY = useMemo(() => {
    const tables =
      floorPlan?.tables.map((x) => ({
        name: x.name,
        coords: x.coords,
        space: x.space,
      })) ?? [];

    return tables.reduce((acc, cV, i, arr) => {
      const touching = tables
        .filter(
          (t) =>
            t.name !== cV.name &&
            t.space === cV.space &&
            overlaps(t.coords, cV.coords, 'y'),
        )
        .map((t) => t.name);

      if (touching.length) {
        return {
          ...acc,
          [cV.name]: touching,
        };
      }

      return acc;
    }, {} as Record<string, string[]>);
  }, [floorPlan]);

  const showTables = !loading && !closed && size !== null;

  const hideShiftMessageForDrag =
    !!experimentalFlags?.data?.switchSpaceOnDrag && isDraggingReservation;

  // Drag-select multiple tables by holding the pointer and sliding across the
  // floor plan. Gated behind the `dragSelectTables` experimental flag so we can
  // pilot it on selected restaurants. The first table touched establishes the
  // intent (add vs. remove); subsequent tables under the pointer follow it.
  const dragSelectEnabled =
    !!experimentalFlags?.data?.dragSelectTables &&
    !!editMode &&
    !assistantActive;

  const selectedTablesRef = useRef<string[]>([]);

  useEffect(() => {
    selectedTablesRef.current = editReservation?.tables ?? [];
  }, [editReservation?.tables]);

  const dragRef = useRef<{
    active: boolean;
    action: 'add' | 'remove';
    visited: Set<string>;
    start: TablePointerStart | null;
  } | null>(null);

  useEffect(() => {
    if (!dragSelectEnabled) return;

    const handleMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag?.active) return;
      if (drag.start && drag.start.pointerId !== e.pointerId) return;

      if (drag.start) {
        const deltaX = e.clientX - drag.start.x;
        const deltaY = e.clientY - drag.start.y;
        const threshold =
          drag.start.pointerType === 'touch'
            ? DRAG_SELECT_TOUCH_START_THRESHOLD_PX
            : DRAG_SELECT_START_THRESHOLD_PX;

        if (deltaX * deltaX + deltaY * deltaY <= threshold * threshold) {
          return;
        }
      }

      const target = document.elementFromPoint(
        e.clientX,
        e.clientY,
      ) as HTMLElement | null;
      const tableEl = target?.closest(
        '[data-table-name]',
      ) as HTMLElement | null;
      if (!tableEl) return;

      const name = tableEl.dataset.tableName;
      const id = tableEl.dataset.tableId;
      const space = tableEl.dataset.tableSpace;
      if (!name || !id || !space) return;
      if (drag.visited.has(name)) return;

      drag.visited.add(name);

      const isSelected = selectedTablesRef.current.includes(name);
      const shouldToggle =
        (drag.action === 'add' && !isSelected) ||
        (drag.action === 'remove' && isSelected);

      if (!shouldToggle) return;

      const available = tableEl.dataset.tableAvailable === '1';
      const switchResa = tableEl.dataset.tableSwitchReservation || undefined;
      if (assistantActive) return;

      onTableClick(
        id,
        name,
        space,
        available,
        switchResa as string | 'blocked' | undefined,
      );
    };

    const handleEnd = () => {
      dragRef.current = null;
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
    };
  }, [assistantActive, dragSelectEnabled, onTableClick]);

  const handleTableClick = useCallback(
    (
      id: string,
      name: string,
      space: string,
      available?: boolean,
      switchResa?: string | 'blocked',
    ) => {
      if (assistantActive) return;

      onTableClick(id, name, space, available, switchResa);
    },
    [assistantActive, onTableClick],
  );

  const handleTableDragStart = useCallback(
    (
      id: string,
      name: string,
      space: string,
      available?: boolean,
      switchResa?: string | 'blocked',
      pointerStart?: TablePointerStart,
    ) => {
      if (assistantActive) return;

      const isSelected = selectedTablesRef.current.includes(name);
      dragRef.current = {
        active: true,
        action: isSelected ? 'remove' : 'add',
        visited: new Set([name]),
        start: pointerStart ?? null,
      };
      handleTableClick(id, name, space, available, switchResa);
    },
    [assistantActive, handleTableClick],
  );

  const alerts = useMemo(
    () =>
      warnings.reduce((acc: string[], cV) => {
        return [...new Set([...acc, ...cV.tables])];
      }, []),
    [warnings],
  );

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      let spaces = floorPlan?.spaces ?? [];
      let index = spaces.findIndex((s) => s.id === space);

      if (index === -1 || eventData.dir === 'Up' || eventData.dir === 'Down')
        return;

      if (eventData.dir === 'Right') {
        if (index > 0) {
          index--;
        } else {
          index = spaces.length - 1;
        }
      } else if (eventData.dir === 'Left') {
        index = (index + 1) % spaces.length;
      }

      onSpaceChange(spaces[index]?.id ?? space);
    },
  });

  const background = useBackground(floorPlan, space);

  return (
    <div id="floorPlan" ref={dimensions.ref}>
      <div id="swippable" {...handlers} onDoubleClick={onCloseSideBar} />
      {!!background && !!size && (
        <img
          src={background.imageUrl}
          id={`background`}
          style={{
            width: (background.width / background.factor) * size,
            height: (background.height / background.factor) * size,
            position: 'absolute',
            zIndex: 0,
            top: background?.offset?.y ?? 0,
            left: background?.offset?.x ?? 0,
            touchAction: 'none',
            filter: background.invert
              ? `invert(${background.invert})`
              : undefined,
            pointerEvents: 'none', // <-- Add this line
          }}
        />
      )}
      {showTables &&
        Object.entries(groupWashesByColor).map(([color, washes]) => (
          // Group-color wash layer: sits above the background image and below
          // every table wrapper (same zIndex 0, earlier in the DOM).
          <div
            key={color}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.25,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            {washes.map((wash) => (
              <div
                key={wash.key}
                className="table-group-bg"
                style={wash.style}
              />
            ))}
          </div>
        ))}
      {showTables &&
        tables.map(({ switchReservation, hidden, ...table }) => (
          <TableWrapper
            // Space-scoped key: while dragging we render every space's tables at
            // once, so key on space+id in case a table id repeats across spaces.
            key={`${table.space}-${table.id}`}
            table={table}
            switchReservation={switchReservation}
            hidden={hidden}
            hasAlert={!editMode && alerts.includes(table.name)}
            touchingX={tablesTouchingX?.[table.name]}
            touchingY={tablesTouchingY?.[table.name]}
            editMode={editMode}
            isSelected={!!editReservation?.tables?.includes(table.name)}
            onTableClick={handleTableClick}
            onTableDragStart={
              dragSelectEnabled ? handleTableDragStart : undefined
            }
            size={size || 0}
            currentTime={currentTime}
            sharedTablePool={sharedTablePool}
            active={
              currentTable === table.id ||
              !!currentReservationTables?.includes(table.name)
            }
          />
        ))}
      {loading && !closed && (
        <Box flex className="center" fullSize>
          <CircularProgress />
        </Box>
      )}
      {closed && (
        <Box flex className="center" fullSize>
          <Typography
            textAlign="center"
            weight="medium"
            color={closed.error ? 'error' : 'textPrimary'}
          >
            {closed.message}
          </Typography>
        </Box>
      )}
      <Box
        flex
        className={`spaceSwitchContainer${
          isDraggingReservation ? ' dragging-reservation' : ''
        }`}
      >
        <SpaceSwitch
          value={space}
          onChange={onSpaceChange}
          spaces={floorPlan?.spaces || []}
          actionsDisabled={assistantActive}
        />
        {showTables && (
          <>
            {/* <SpaceSwitch
            title="Bevorzugter Bereich"
            value={Shuffle.state?.preffered}
            onChange={onSpaceChange}
            spaces={floorPlan?.spaces || []}
          /> */}

            {!Shuffle.state && !hideShiftMessageForDrag && <ShiftMessage />}

            {!!Shuffle.state && (
              <>
                <div style={{ flexGrow: 10 }} />
                <Button
                  variant="default"
                  onClick={Shuffle.closeShuffle}
                  style={{ marginRight: 16 }}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  loading={Shuffle?.state?.loading}
                  onClick={
                    !!Shuffle.state?.reservations?.length
                      ? Shuffle.submitShuffle
                      : Shuffle.requestShuffle
                  }
                >
                  {!!Shuffle.state?.reservations?.length
                    ? 'Änderung speichern'
                    : 'Neu Anordnen'}
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </div>
  );
};

export default FloorPlan;
